type SettleOptions = {
  // How many frames the height must stay the same to count as "done animating".
  stableFrames?: number;
  // Keep scrolling for at least this many frames before allowing a stop. Covers the short pause
  // before Ionic starts the accordion animation, so we don't stop too early. ~24 frames ≈ 400ms.
  minFrames?: number;
  // Safety limit so the loop always ends, even if the height never settles.
  maxFrames?: number;
};

// Re-centers the menu on every frame until the accordions stop animating, then stops.
// We re-apply each frame (not once) so the item stays centered as the menu grows/shrinks.
// Cancellable: a new run aborts the signal and this loop ends right away.
export const applyMenuScrollUntilSettled = (
  container: HTMLElement,
  apply: () => void,
  signal: AbortSignal,
  { stableFrames = 3, minFrames = 24, maxFrames = 90 }: SettleOptions = {}
): Promise<void> =>
  new Promise((resolve) => {
    // Already cancelled before we start: do nothing.
    if (signal.aborted) {
      resolve();
      return;
    }

    let lastHeight = -1;
    let stable = 0;
    let frames = 0;
    let rafId = 0;

    // Cancelled mid-run: stop the next frame and finish.
    const onAbort = () => {
      cancelAnimationFrame(rafId);
      resolve();
    };
    signal.addEventListener('abort', onAbort, { once: true });

    const tick = () => {
      // 1. Scroll the item into view for this frame.
      apply();

      // 2. Count how many frames in a row the height hasn't changed.
      const height = container.scrollHeight;
      if (height === lastHeight) {
        stable += 1;
      } else {
        stable = 0;
        lastHeight = height;
      }
      frames += 1;

      // 3. Stop once the height held steady long enough (but not before minFrames), or we hit the cap.
      const settled = frames >= minFrames && stable >= stableFrames;
      if (settled || frames >= maxFrames) {
        signal.removeEventListener('abort', onAbort);
        resolve();
        return;
      }

      // 4. Otherwise keep going next frame.
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
  });
